/**
 * Queue growth — §5.6.
 *
 * GROWTH RULE: keep exactly ONE PAGE QUEUED AHEAD of the page being recited.
 *
 * Append EARLY — that is what makes the page turn gapless. The next page is almost
 * always the same surah, so its first ayah is already sounding in the file that is
 * playing. With the keys already in the queue, the rAF loop simply lets the index
 * follow the stream: no seek, no reload, nothing to resume. Waiting for the last ayah
 * to end leaves a stall to cover.
 */
import { useEffect, useRef } from 'react'
import { TOTAL_PAGES, ayahKeysOnPage, pageOfVerse, type Meta } from '@mushaf/corpus'
import { usePlayer } from '../stores/player'
import { useSettings } from '../stores/settings'

export function useQueueGrowth(meta: Meta | undefined) {
  const queue = usePlayer((s) => s.queue)
  const kind = usePlayer((s) => s.kind)
  const currentKey = usePlayer((s) => s.currentKey)
  const passageRepeatsLeft = usePlayer((s) => s.passageRepeatsLeft)
  const extendQueue = usePlayer((s) => s.extendQueue)
  const autoAdvancePage = useSettings((s) => s.autoAdvancePage)

  /** Track the in-flight page so two ticks do not queue it twice. */
  const inFlight = useRef<number | null>(null)

  useEffect(() => {
    if (!meta || !currentKey) return

    // A13 — a `passage` queue NEVER grows.
    if (kind !== 'page') return
    if (!autoAdvancePage) return
    // A page appended while a passage repeat is armed would make the loop cover two
    // pages, permanently, at ∞.
    if (passageRepeatsLeft > 0) return

    const recitedPage = pageOfVerse(meta.pages, currentKey)
    const tailKey = queue[queue.length - 1]
    const tailPage = tailKey ? pageOfVerse(meta.pages, tailKey) : null
    if (recitedPage === null || tailPage === null) return

    // Already one page ahead.
    if (tailPage > recitedPage) return
    const nextPage = recitedPage + 1
    if (nextPage > TOTAL_PAGES) return
    if (inFlight.current === nextPage) return

    inFlight.current = nextPage
    const keys = ayahKeysOnPage(meta, nextPage)
    if (keys.length > 0) extendQueue(keys)
    inFlight.current = null

    /**
     * DO NOT GATE GROWTH ON `isPlaying`.
     *
     * The usual way to reach the end of a page queue is to have auto-advance OFF, so
     * the toggle gets flipped on while paused at the bottom of the page. Queuing the
     * next page right then, rather than on the resume, is what lets that resume carry
     * straight on (T20).
     */
  }, [meta, currentKey, queue, kind, autoAdvancePage, passageRepeatsLeft, extendQueue])
}

/**
 * SHRINK RULE — anything that ends a page queue must give the queue its end back
 * (§5.6, A11).
 *
 * Because the next page is appended long before it is reached, by the time the user
 * acts the queue already runs past the current page:
 *
 *  - Auto-advance switched off: without a trim, recitation carries on into the
 *    already-appended page anyway. Symptom: "the toggle only works after a refresh."
 *  - A page repeat armed: growth stops, but a page appended BEFORE the repeat was set
 *    is still queued, so the loop covers two pages — permanently, at ∞.
 *
 * Trimming the tail alone is what the auto-advance-off path wants: it keeps the
 * previous page reachable with "previous ayah".
 */
export function useQueueTrimOnAutoAdvanceOff(meta: Meta | undefined) {
  const autoAdvancePage = useSettings((s) => s.autoAdvancePage)
  const previous = useRef(autoAdvancePage)

  useEffect(() => {
    const wasOn = previous.current
    previous.current = autoAdvancePage
    if (wasOn === autoAdvancePage || autoAdvancePage || !meta) return

    const { queue, currentKey, kind, trimQueue } = usePlayer.getState()
    if (kind !== 'page' || !currentKey) return

    const recitedPage = pageOfVerse(meta.pages, currentKey)
    if (recitedPage === null) return

    // Everything up to and including the last key of the recited page.
    let cut = 0
    for (let i = 0; i < queue.length; i += 1) {
      if (pageOfVerse(meta.pages, queue[i]!) === recitedPage) cut = i + 1
    }
    // A14: trimQueue itself refuses to cut away the sounding ayah.
    if (cut > 0) trimQueue(cut)
  }, [autoAdvancePage, meta])
}
